<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.personName" placeholder="海外人员名称" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.idType"
          filterable
          placeholder="证件类型"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option
            v-for="(label, value) in settlementDetailIdTypeLabel"
            :key="value"
            :label="label"
            :value="value"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-input v-model="params.idNo" placeholder="证件号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.personTel" placeholder="手机号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.serverId"
          filterable
          placeholder="合作服务商"
          clearable
        >
          <el-option
            v-for="item in optionsServer"
            :key="item.id"
            :label="item.serverName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.agentTopId"
          filterable
          placeholder="所属特代"
          clearable
        >
          <el-option
            v-for="item in optionsAgent"
            :key="item.id"
            :label="item.agentName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.companyId"
          placeholder="所属企业"
          clearable
          filterable
        >
          <el-option
            v-for="item in enterpriseOptions"
            :key="item.id"
            :label="item.companyName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      
      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 表格数据 -->
    <el-table 
      ref="overseasPersonnelTable" 
      v-loading="loading" 
      class="normal-table" 
      :border="true" 
      :scrollbar-always-on="true" 
      :data="tableData" 
      :cell-style="initOperatorColumnStyle"
    >
      <el-table-column align="left" prop="personName" label="海外人员名称" :width="180" show-overflow-tooltip />
      <el-table-column align="left" prop="idType" label="证件类型" :min-width="180" show-overflow-tooltip>
        <template #default="scope">
          {{ isNull(scope.row.idType) ? '--' : settlementDetailIdTypeLabel[scope.row.idType] }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="idNo" label="证件号" :min-width="180" show-overflow-tooltip />
      <el-table-column align="left" prop="bankCard" label="银行卡号" :formatter="(row) => isNull(row.bankCard) ? '--' : row.bankCard" :min-width="180" show-overflow-tooltip />
      <el-table-column align="left" prop="personTel" label="手机号" :min-width="150" show-overflow-tooltip />
      <el-table-column align="left" prop="alipayAccount" label="支付宝号" :formatter="(row) => isNull(row.alipayAccount) ? '--' : row.alipayAccount" :min-width="150" show-overflow-tooltip />
      <el-table-column align="left" prop="serverNames" label="合作服务商" :min-width="260" show-overflow-tooltip />
      <el-table-column align="left" prop="agentTopNames" label="所属特代" :min-width="260" show-overflow-tooltip />
      <el-table-column align="left" prop="companyNames" label="所属企业" :min-width="260" show-overflow-tooltip />
      <el-table-column align="left" prop="customerNo" label="海外人员编号" :min-width="180" show-overflow-tooltip />

      <el-table-column
        v-if="buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { initOperatorColumnStyle, SvgIcon, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  queryOverseasPersonnelPage,
  queryServerList,
  queryAgentList,
  queryEnterpriseList,
} from '@core/main';
import {
  IAgentOptionsItem,
  IEnterpriseItem,
  IOverseasPersonnelItem,
  IOverseasPersonneParams,
  IServerOptionsItem,
  settlementDetailIdTypeLabel,
} from '@core/main/types';
import { isNull } from '@core/tools';
import { useRouter } from 'vue-router';

defineOptions({ name: 'OverseasPersonnel' });

const router = useRouter();

const defaultPageSize = 12;

const params = reactive<IOverseasPersonneParams>({
  page: 1,
  size: defaultPageSize,
});
const loading = ref(false);
const total = ref(0);
const tableData = ref<IOverseasPersonnelItem[]>([]);
const optionsServer = ref<IServerOptionsItem[]>([]);
const optionsAgent = ref<IAgentOptionsItem[]>([]);
const enterpriseOptions = ref<IEnterpriseItem[]>([]);

const { buttons } = useTblPermissionButtons([
  {
    name: '详情',
    code: '',
    action: doDetail,
  },
  {
    name: '编辑',
    code: 'oversea-customer:edit',
    action: doEdit,
  },
]);

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryOverseasPersonnelPage(params);
    
    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

function doDetail(row: IOverseasPersonnelItem) {
  router.push({
    name: 'OverseasPersonnelManage',
    query: {
      type: 'detail',
      id: row.id,
    },
  });
}

function doEdit(row: IOverseasPersonnelItem) {
  router.push({
    name: 'OverseasPersonnelManage',
    query: {
      type: 'edit',
      id: row.id,
    },
  });
}

async function getServerList() {
  try {
    const { data } = await queryServerList();

    optionsServer.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function getAgentList() {
  try {
    const { data } = await queryAgentList();

    optionsAgent.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function getEnterpriseList() {
  try {
    const { data } = await queryEnterpriseList();

    enterpriseOptions.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function init() {
  getServerList();
  getAgentList();
  getEnterpriseList(); 
  fetchData();
}

init();
</script>
